import React, { createContext } from "react";
import { useEffect, useState } from "react";
import * as Database from "../../config/Firebase";
import { Customer } from "../../models/Customer";
import { FirebaseObject } from "../../models/FirebasePostObject";
import { Business } from "../../models/Business";
import { ILivestockUser } from "../../models/ILivestockUser";
import { useApi } from "../../hooks/useApi";
import { fetchCustomers } from "../../util/api";
import { useStore } from "../../store/useStore";
import { CustomerObjectMapTypeResponseData } from "../../@types";

type State = {
  customers: CustomerObjectMapType[] | null;
  refetch: (props: RefetchProps) => void;
  loading: boolean;
};

const initialState: State = {
  customers: null,
  refetch: () => {
    throw new Error("Refetch not injected yet");
  },
  loading: true,
};

export type CustomerObjectMapType = Pick<
  Customer,
  | "email"
  | "familyName"
  | "givenName"
  | "userStatus"
  | "userCreateDate"
  | "phoneNumber"
  | "sub"
  | "businesses"
> &
  Pick<ILivestockUser, "businesses" | "gifts" | "hasCompletedSetup"> & {
    business?: FirebaseObject<Business>;
  } & {
    "custom:alpha2"?: string;
  };

export type CacheRelatableSnapshot = {
  business: FirebaseObject<Business>;
  code: string;
};

export type RefetchProps = {
  clearData?: boolean;
};

const sorter = (a: CustomerObjectMapType, b: CustomerObjectMapType): number => {
  return a.email.localeCompare(b.email);
};

const context = createContext<State>(initialState);

const CustomerProvider: React.FC = ({ children }) => {
  const { state } = useStore();
  const [
    executeRequest,
    { data: customerList, refetch },
  ] = useApi<CustomerObjectMapTypeResponseData>(fetchCustomers());
  const [requested, setRequested] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CustomerObjectMapType[] | null>(null);
  // The current chunk represents one paginated dataset
  const [currentChunk, setCurrentChunk] = useState<
    CustomerObjectMapType[] | null
  >(null);
  // All the chunks loaded thus far
  const [dataChunks, setDataChunks] = useState<Array<CustomerObjectMapType[]>>(
    []
  );
  const [
    firebaseSnapshot,
    setFirebaseSnapshot,
  ] = useState<CacheRelatableSnapshot | null>(null);
  const [readyForListeners, setReadyForListeners] = useState<boolean>(false);

  const _refetch = ({ clearData }: RefetchProps) => {
    if (clearData) {
      setData(null);
      setDataChunks([]);
    }
    refetch(fetchCustomers());
  };

  /**
   * This hook caches each "chunk" of data.
   */
  useEffect(() => {
    if (!customerList) return;

    setCurrentChunk(customerList.customers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerList]);

  /**
   * Whenever a new chunk is ready to be processed, this hook fires off
   * It handles merging of the chunk, clearing up the current chunk and
   * reftches the next chunk, if any
   */
  useEffect(() => {
    if (!currentChunk) return;
    setDataChunks([...dataChunks, currentChunk]);
    setCurrentChunk(null);
    if (customerList?.paginationToken) {
      refetch(fetchCustomers(customerList.paginationToken));
    } else {
      setLoading(false);
    }
  }, [currentChunk]);

  /**
   * When all chunks have been acquired, we can concat the array of chunks
   * and flatten it into the data array
   */
  useEffect(() => {
    if (!dataChunks) return;
    if (customerList?.paginationToken) return;
    const d: CustomerObjectMapType[] = [];
    dataChunks?.forEach((chunk) => {
      d.push(...chunk);
    });
    setData(d.sort(sorter));
    setReadyForListeners(true);
  }, [customerList?.paginationToken, dataChunks]);

  /**
   * Once the initial data is set, we can then attach the firebase listeners to those data objects
   * and update them whenever something changes
   */
  useEffect(() => {
    if (customerList?.paginationToken) return;
    if (!readyForListeners || !customerList?.customers || !data) return;
    for (const customer of data) {
      /**
       * Add a database event listener if businesses get updated
       */
      customer.businesses?.forEach(function (business, i) {
        Database.ref(`/${business.code}`).then((b) => {
          b.once("value", async (obj) => {
            const tree: {
              Business?: {
                [key: string]: FirebaseObject<Business>;
              };
            } | null = obj.val();

            if (tree?.Business) {
              const firebaseBusiness: FirebaseObject<Business> = Object.entries(
                tree.Business
              )[0][1];
              firebaseBusiness.data.attributes.subscription =
                business.subscription;
              firebaseBusiness.data.attributes.code = business.code;
              firebaseBusiness.data.attributes.isOwner = business.isOwner;

              setFirebaseSnapshot({
                code: business.code,
                business: firebaseBusiness,
              });
            }
          });
        });
      });
    }
    setReadyForListeners(false);
  }, [readyForListeners, customerList]);

  useEffect(() => {
    if (!state.ilivestockUser) return;
    if (requested) return;

    executeRequest();
    setRequested(true);

    /**
     * Disconnect the Firebase listeners
     */
    return function cleanUp() {
      if (!customerList) return;
      for (const customer of customerList.customers) {
        customer.businesses?.forEach((business) => {
          Database.ref(`/${business.code}`).then((b) => b.off());
        });
      }
    };
  }, [state.ilivestockUser, requested]);

  useEffect(() => {
    if (customerList?.paginationToken) return;

    if (!firebaseSnapshot || !data) return;
    const customer = data.find(
      (c) => c.sub === firebaseSnapshot.business.meta.exportOwner
    );
    if (!customer) return;

    const business = customer.businesses?.find(
      (b) => b.code === firebaseSnapshot.code
    );
    if (!business) return;

    const dataCopy = data.filter((c) => c.sub !== customer.sub);

    // Map non firebase properties
    firebaseSnapshot.business.data.attributes.subscription =
      business.subscription;
    firebaseSnapshot.business.data.attributes.code = business.code;
    // add the new data object to the array
    dataCopy.push({ ...customer, business: firebaseSnapshot.business });
    // We just overwrite the entire array as it is cheap
    // const filteredData = data.filter((d) => d.sub !== customer.sub);
    setData(dataCopy.sort(sorter));
    setFirebaseSnapshot(null);
  }, [firebaseSnapshot]);

  return (
    <context.Provider
      value={{ customers: data, refetch: _refetch, loading: loading }}
    >
      {children}
    </context.Provider>
  );
};

export default CustomerProvider;
export { context };
